import { ApiValidationError } from "../errors";
import { IncomingMessage } from "http";
import { z } from "zod";
import axios, { AxiosProgressEvent, AxiosRequestConfig, Method, ResponseType } from "axios";
import axiosRetry from "axios-retry";

export const queryKeys = {
  profiles: {
    byId: (id: string) => ['profiles', { id }],
  },
  publicProfiles: {
    byId: (id: string) => ['public-profiles', { id }],
    all: ['public-profiles'],
  },
  regions: {
    byTrackId: (trackId: string) => ['regions', { trackId }],
    bySongId: (songId: string) => ['regions', { songId }],
  },
  waveforms: {
    bySongId: (songId: string) => ['waveforms', { songId }],
  },
  playlists: {
    root: ['playlists'],
    byUserId: (userId: string) => ['playlists', { userId }],
    byId: (id: string) => ['playlists', { id }],
  },
  songs: {
    root: ['songs'],
    byId: (id: string) => ['songs', { id }],
    byUserId: (userId: string) => ['songs', { userId }],
    byPlaylistId: (playlistId: string) => ['songs', { playlistId }],
    all: (params: { filters: any, page: number, pageSize: number }) => ['songs', 'all', params],
  },
  tracks: {
    bySongId: (songId: string) => ['tracks', { songId }],
  },
  markers: {
    bySongId: (songId: string) => ['markers', { songId }],
  },
  dismissedBlocks: {
    byUserId: (userId: string) => ['dismissed-blocks', { userId }],
  },
  songViewCounts: {
    bySongId: (songId: string) => ['song-view-counts', { songId }],
  },
  isSuperuser: {
    byUserId: (userId: string) => ['is-superuser', { userId }],
  },
  adminUsers: {
    all: (params: { filters: any, page: number, pageSize: number }) => ['admin-users', 'all', params],
  },
}

export interface QueryOptions {
  secret?: string;
}

export const SECRET_HEADER = "Secret";

async function parseAndValidateWithSchema<T>(schema: z.ZodType<T>, data: unknown) {
  const result = schema.safeParse(data);
  if (!result.success) throw new ApiValidationError(result.error.message);
  return result.data;
}

export async function request<T>({
  path,
  method,
  body,
  schema,
  params,
  responseType,
  onUploadProgress,
  onDownloadProgress,
}: {
  req?: IncomingMessage;
  path: string;
  method: Method;
  body?: Object;
  params?: Object;
  schema?: any;
  responseType?: ResponseType;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
}): Promise<[T, string | null]> {
  const url = path;

  const requestConfig: AxiosRequestConfig = {
    method,
    url,
    data: body,
    params,
    responseType,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    onUploadProgress,
    onDownloadProgress,
  };

  try {
    const client = axios.create();
    axiosRetry(client, { retries: 3 });

    const response = await client(requestConfig)

    if (response.status === 204) {
      return [null as T, null];
    }

    const data = schema ? parseAndValidateWithSchema<T>(schema, response.data) : response.data

    let contentType = response.headers["content-type"]?.toString() || response.headers["Content-Type"]?.toString() || null;

    return [data, contentType]
  } catch (e) {
    if (e.response) {
      // Redirect all 401s and return
      // if (status === 401) {
      //   if (process.env.NODE_ENV === "development") {
      //     console.log("Redirecting to login", url);
      //   }
      //   return window.location.assign(getLoginUrl(window.location.toString()));
      // }
      // Redirect all 403s and return
      // if (status === 403) {
      //   if (process.env.NODE_ENV === "development") {
      //     console.log("Redirecting to 403 error page");
      //   }
      //   return window.location.assign("/app/error-403");
      // }
      // Send error notification if a non-403/404(/401)
      // if (status !== 403 && status !== 404) {
      //   reportError(e);
      // }
      if (process.env.NODE_ENV === "development") {
        console.log("Response error", path, e);
      }
    } else if (e.request) {
      if (process.env.NODE_ENV === "development") {
        console.log("Request error", path, e);
      }
    } else {
      if (process.env.NODE_ENV === "development") {
        console.log("Unknown error", path, e);
      }
    }

    // TODO: handle different errors differently
    // Rethrow all non-401s
    throw e;
  }
}

export const getBlob = async (url: string, onProgress?: (e: AxiosProgressEvent) => void): Promise<Blob> => {
  const [data, contentType] = await request<string>({
    path: url,
    method: "get",
    responseType: "blob",
    onDownloadProgress: onProgress,
  });

  return new Blob([data], { type: contentType || undefined });
};
